import { getColorMap } from '@src/utils/colors';
import {
  FILTER_FACETS,
  FilterFacetType,
  FilterFacet,
  FUNCTION_ORDER,
  getStatusBucket,
  Product,
  SortDirection,
  SortDirectionType,
  SortField,
  SortFieldType,
  STATUS_BUCKETS,
  StatusBucket,
  StatusBucketDef
} from './product-roadmap-constants';

export type FacetSelection = Record<FilterFacetType, ReadonlySet<string>>;

export const emptyFacetSelection = (): FacetSelection =>
  FILTER_FACETS.reduce(
    (acc, facet) => ({ ...acc, [facet]: new Set<string>() }),
    {} as FacetSelection
  );

// What the page opens with: every status except Deployed (Sean, 2026-10-19: deployed products
// aren't really part of a roadmap; keep them one click away, hidden on load). Selections hold
// raw `life` values, so this lists every non-deployed one present in the data. If nothing is
// deployed, or everything is, it stays empty -- no filter.
export const defaultFacetSelection = (products: Product[]): FacetSelection => {
  const lives = new Set(products.map(p => p.life));
  const shown = [...lives].filter(life => getStatusBucket(life).key !== StatusBucket.DEPLOYED);
  const selection = emptyFacetSelection();
  if (shown.length === 0 || shown.length === lives.size) return selection;
  return { ...selection, [FilterFacet.STATUS]: new Set(shown) };
};

// A facet's selection is normally "which values are included" (empty = no filter, show all).
// That representation has no way to mean "explicitly show nothing for this facet" -- an empty
// Set already means "show everything" -- so a "None" quick-action needs a distinct marker.
// Embeds a newline, which no product name/kind/scope/audience/status value contains, so it can
// never collide with real data.
export const NONE_SELECTED = '\n__none__\n';

const facetValues = (product: Product, facet: FilterFacetType): string[] => {
  switch (facet) {
    // No parent -- e.g. a Program like Holistic ROAS, or a genuinely standalone product --
    // contributes no value, so it never matches a specific Parent selection (same as any other
    // facet: an empty value can't satisfy a chosen filter) without needing a placeholder like
    // "(none)" cluttering the option list.
    case FilterFacet.PARENT:
      return product.parent ? [product.parent] : [];
    case FilterFacet.FUNCTION:
      return product.function;
    // Multi-value and frequently empty -- 31 of 71 products carry no tag, and they contribute no
    // value rather than a "(none)" option, exactly as PARENT does above. `?? []` also covers data
    // published before the generator emitted this field at all.
    case FilterFacet.INTELLIGENCE:
      return product.intelligence ?? [];
    case FilterFacet.STATUS:
    default:
      return [product.life];
  }
};

// A facet with nothing selected passes every product (no filter applied yet). NONE_SELECTED
// means the opposite extreme -- explicitly excludes every product. Otherwise a facet requires
// at least one of the product's values to be selected -- a union match within the facet (e.g.
// Function: Insights OR Automation), while facets are ANDed together.
const facetPasses = (
  product: Product,
  facet: FilterFacetType,
  selected: ReadonlySet<string>
): boolean => {
  if (selected.has(NONE_SELECTED)) return false;
  if (selected.size === 0) return true;
  return facetValues(product, facet).some(v => selected.has(v));
};

export const filterProducts = (products: Product[], selection: FacetSelection): Product[] =>
  products.filter(p => FILTER_FACETS.every(facet => facetPasses(p, facet, selection[facet])));

// For each facet, the product set filtered by every *other* facet's current selection --
// feeds `distinctFacetValues` so a facet's own chip counts reflect the rest of the filter bar
// without collapsing to zero the moment that facet itself has a selection.
export const scopedProductsByFacet = (
  products: Product[],
  selection: FacetSelection
): Record<FilterFacetType, Product[]> =>
  FILTER_FACETS.reduce(
    (acc, facet) => ({
      ...acc,
      [facet]: products.filter(p =>
        FILTER_FACETS.every(other => other === facet || facetPasses(p, other, selection[other]))
      )
    }),
    {} as Record<FilterFacetType, Product[]>
  );

// Distinct values for a facet, each with the count of products matching the *other* facets'
// current selection (so chip counts reflect the live filtered set, not the full dataset) --
// `scopedProducts` should already be filtered by every facet except this one.
export const distinctFacetValues = (
  scopedProducts: Product[],
  facet: FilterFacetType
): { value: string; count: number }[] => {
  const counts = new Map<string, number>();
  scopedProducts.forEach(p => {
    facetValues(p, facet).forEach(v => counts.set(v, (counts.get(v) ?? 0) + 1));
  });

  const entries = Array.from(counts.entries()).map(([value, count]) => ({ value, count }));
  if (facet === FilterFacet.FUNCTION) {
    return entries.sort(
      (a, b) => FUNCTION_ORDER.indexOf(a.value) - FUNCTION_ORDER.indexOf(b.value)
    );
  }
  return entries.sort((a, b) => a.value.localeCompare(b.value));
};

// Status, canonicalized: groups the fixture's ~12 raw `life` synonyms (Deployed/Live/Shipped/
// Shipping all mean "done", etc.) down to the 6 buckets the retired HTML prototype's status
// strip showed, in that same fixed display order -- one chip per bucket, not per raw label.
export interface StatusBucketOption {
  bucket: StatusBucketDef;
  rawValues: string[];
  count: number;
}

export const distinctStatusBuckets = (scopedProducts: Product[]): StatusBucketOption[] => {
  const rawValuesByBucket = new Map<string, Set<string>>();
  const countByBucket = new Map<string, number>();
  scopedProducts.forEach(p => {
    const bucket = getStatusBucket(p.life);
    countByBucket.set(bucket.key, (countByBucket.get(bucket.key) ?? 0) + 1);
    if (!rawValuesByBucket.has(bucket.key)) {
      rawValuesByBucket.set(bucket.key, new Set());
    }
    rawValuesByBucket.get(bucket.key)?.add(p.life);
  });

  return STATUS_BUCKETS.filter(bucket => countByBucket.has(bucket.key)).map(bucket => ({
    bucket,
    rawValues: Array.from(rawValuesByBucket.get(bucket.key) ?? []),
    count: countByBucket.get(bucket.key) ?? 0
  }));
};

// name -> hex, via the shared categorical palette (colors.ts) -- Function is the one remaining
// open-ended-cardinality facet still using this (Kind/Audience/Scope now render as small
// solid-fill icon chips with their own hand-picked, fixed palette instead -- see
// KIND_ICON/AUDIENCE_ICON/SCOPE_ICON in ProductRoadmapTable.tsx).
export const facetColorMap = (values: string[]): Record<string, string> =>
  getColorMap(values.map(name => ({ name })));

// The full description, for every place the table shows or matches it -- the row snippet, its
// hover tooltip, the Description search and the Description sort. `oneLiner` is the generator's
// first-sentence cut, and since descriptions became problem-first (product-brain, 2026-10-01)
// that first sentence is the *problem* alone, never what the product does. Using it anywhere in
// the table hid the second half of every description; the card already showed the full text.
// Falls back to `oneLiner` only for a payload that somehow carries no `desc`.
export const fullDescription = (product: Product): string => product.desc || product.oneLiner || '';

// Fields the Description column's search box matches against. `desc` is the full text, so a
// phrase from the second sentence ("Holistic ROAS" on MMM for Foundation) is found; matching
// `oneLiner` alone missed it.
export const DESCRIPTION_SEARCH_KEYS: (keyof Product)[] = ['desc', 'oneLiner'];

// Should a click on a table row open its detail card? The whole row is a mouse/touch target so
// the full description is one click from its snippet -- but two clicks must be left alone:
//  - one that landed on an interactive element inside the row (today, the product name). It
//    opens the card itself, and also keeps the row's single keyboard / screen-reader control, so
//    the row deliberately has no role or tabIndex of its own (see InteractiveEl on not
//    relabelling rows as buttons);
//  - the end of a drag-select, where someone is copying text, not asking for the card.
export const shouldRowClickOpenCard = (
  target: EventTarget | null,
  selectedText: string
): boolean => {
  if (selectedText.trim() !== '') return false;
  if (!(target instanceof Element)) return true;
  return target.closest('[role="button"], a, button, input, select, textarea') === null;
};

const getSortValue = (product: Product, field: SortFieldType): string => {
  switch (field) {
    case SortField.PARENT:
      return product.parent;
    case SortField.DESCRIPTION:
      return fullDescription(product);
    case SortField.FUNCTION:
      return product.function[0] ?? '';
    case SortField.STATUS:
      return product.life;
    case SortField.TARGET:
      return product.target;
    case SortField.OWNER:
      return product.owner;
    case SortField.NAME:
    default:
      return product.name;
  }
};

export const sortProducts = (
  products: Product[],
  field: SortFieldType,
  direction: SortDirectionType
): Product[] =>
  [...products].sort((a, b) => {
    const aVal = getSortValue(a, field).toLowerCase();
    const bVal = getSortValue(b, field).toLowerCase();
    if (aVal < bVal) return direction === SortDirection.ASC ? -1 : 1;
    if (aVal > bVal) return direction === SortDirection.ASC ? 1 : -1;
    return 0;
  });
